import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, Truck } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { ConfirmDialog, EmptyState, Field, Loading, PageHeader, SimpleSelect, btnGhost, btnPrimary, inputCls } from "@/components/app/common";
import { PAESI, REGIMI, REGIME_SHORT, regimeFor } from "@/lib/fiscale";

const empty = { nome: "", piva: "", email: "", note: "", paese: "IT", regime_default: "auto" };

function FornitoreDialog({ open, onOpenChange, item, onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  useEffect(() => { if (open) setF(item ? { nome: item.nome, piva: item.piva, email: item.email, note: item.note, paese: item.paese || "IT", regime_default: item.regime_default || "auto" } : empty); }, [open, item]);
  const save = async () => {
    if (!f.nome.trim()) return toast.error("Inserisci il nome del fornitore");
    setSaving(true);
    const body = { ...f, regime_default: f.regime_default === "auto" ? null : f.regime_default };
    try {
      if (item) await api.put(`/fornitori/${item.id}`, body); else await api.post("/fornitori", body);
      toast.success(item ? "Fornitore aggiornato" : "Fornitore creato");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="fornitore-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display font-medium text-2xl">{item ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Ragione sociale"><Input data-testid="fornitore-nome-input" value={f.nome} onChange={set("nome")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="P.IVA"><Input data-testid="fornitore-piva-input" value={f.piva} onChange={set("piva")} className={inputCls} /></Field>
            <Field label="Email"><Input data-testid="fornitore-email-input" value={f.email} onChange={set("email")} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Paese"><SimpleSelect testId="fornitore-paese-select" value={f.paese} onChange={(v) => setF((p) => ({ ...p, paese: v }))} options={PAESI.map(([value, label]) => ({ value, label: `${value} · ${label}` }))} /></Field>
            <Field label="Regime IVA predefinito"><SimpleSelect testId="fornitore-regime-select" value={f.regime_default} onChange={(v) => setF((p) => ({ ...p, regime_default: v }))} options={[{ value: "auto", label: `Automatico: ${REGIME_SHORT[regimeFor(f.paese)]}` }, ...REGIMI.map(([value, label]) => ({ value, label }))]} /></Field>
          </div>
          <Field label="Note"><Textarea data-testid="fornitore-note-input" value={f.note} onChange={set("note")} className="rounded-xl border-[#EAE3DC]" rows={3} /></Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className={btnPrimary} onClick={save} disabled={saving} data-testid="fornitore-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Fornitori() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/fornitori").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const remove = async () => {
    try { await api.delete(`/fornitori/${del.id}`); toast.success("Fornitore eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  return (
    <div data-testid="fornitori-page">
      <PageHeader overline="Acquisti" title="Fornitori" subtitle="L'anagrafica dei tuoi fornitori, usata nelle fatture di acquisto.">
        <Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })} data-testid="add-fornitore-button"><Plus className="h-4 w-4 mr-1" />Nuovo fornitore</Button>
      </PageHeader>
      {!items ? <Loading /> : !items.length ? (
        <EmptyState icon={Truck} title="Nessun fornitore" text="Aggiungi il primo fornitore oppure caricane uno direttamente da una fattura." />
      ) : (
        <div className="card-soft overflow-x-auto" data-testid="fornitori-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
              <tr>{["Fornitore", "P.IVA", "Paese", "Regime IVA", "Email", "Fatture", "Note", ""].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#EAE3DC]">
              {items.map((f, i) => (
                <tr key={f.id} data-testid={`fornitore-row-${i}`} className="hover:bg-[#FAF7F5]">
                  <td className="px-4 py-3 font-medium">{f.nome}</td>
                  <td className="px-4 py-3 tabular">{f.piva || "—"}</td>
                  <td className="px-4 py-3" data-testid={`fornitore-paese-${i}`}>{f.paese || "IT"}</td>
                  <td className="px-4 py-3" data-testid={`fornitore-regime-${i}`}><span className="text-xs px-2 py-0.5 rounded-full bg-[#F4EFEA]">{REGIME_SHORT[f.regime_proposto] || "Ordinaria"}</span></td>
                  <td className="px-4 py-3 text-[#5E4A42]">{f.email || "—"}</td>
                  <td className="px-4 py-3 tabular">{f.n_fatture}</td>
                  <td className="px-4 py-3 text-xs text-[#756B64] max-w-[260px] truncate">{f.note || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`edit-fornitore-${i}`} onClick={() => setEdit({ open: true, item: f })} className="p-2 rounded-lg hover:bg-[#F4EFEA] text-[#756B64]"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-fornitore-${i}`} onClick={() => setDel(f)} className="p-2 rounded-lg hover:bg-[#E07A5F]/10 text-[#B4553D]"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FornitoreDialog open={edit.open} item={edit.item} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare il fornitore?" text={del?.nome} onConfirm={remove} />
    </div>
  );
}
